import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, CheckCircle2, XCircle, Info } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { CAT, CRITERIA, LEVELS, levelFor } from "@/lib/ratings";

function LevelPicker({ k, label, value, onChange }) {
  const lv = levelFor(value);
  return (
    <div data-testid={`criterion-${k}`}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-sm font-semibold">{label}{k === "sportsmanship" && <span className="ml-2 text-[10px] mono uppercase" style={{ color: "var(--text-2)" }}>separate from skill</span>}</span>
        <span className="mono font-bold text-sm" data-testid={`score-${k}-value`}>{value == null ? "—" : `${value.toFixed(2)} · ${lv.name}`}</span>
      </div>
      <input type="range" min="0" max="6.5" step="0.05" value={value ?? 2.5} data-testid={`score-${k}-slider`}
        onChange={(e) => onChange(Number(e.target.value))} className="w-full accent-lime-500" />
      <div className="flex flex-wrap gap-1 mt-1.5">
        {LEVELS.map(([min, name]) => (
          <button key={name} type="button" data-testid={`score-${k}-level-${min}`} onClick={() => onChange(min === 5.5 ? 5.75 : min + 0.1)}
            className="rounded-full px-2 py-0.5 text-[10px] font-bold transition-colors"
            style={lv?.name === name ? { background: "var(--primary)", color: "var(--on-primary)" } : { background: "var(--bg)", color: "var(--text-2)", border: "1px solid var(--border)" }}>{min.toFixed(min % 1 ? 2 : 1)} {name}</button>
        ))}
      </div>
      {lv && <p className="text-xs mt-1.5 leading-relaxed" style={{ color: "var(--text-2)" }} data-testid={`score-${k}-desc`}>{lv.desc}</p>}
    </div>
  );
}

export function EligibilityList({ el }) {
  return (
    <div className="space-y-1.5" data-testid="eligibility-list">
      {el.checks.map((c) => (
        <div key={c.key} className="flex items-start gap-2 text-sm" data-testid={`check-${c.key}`}>
          {c.ok ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#22c55e" }} /> : <XCircle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#ef4444" }} />}
          <span>{c.label}{c.detail && <span className="block text-xs" style={{ color: c.ok ? "var(--text-2)" : "#ef4444" }}>{c.detail}</span>}</span>
        </div>
      ))}
    </div>
  );
}

export default function RatingForm({ playerId, token, el, onDone, submitUrl = "/ratings/submit", note, casual = false }) {
  const prev = el.existing;
  const [scores, setScores] = useState(prev ? { ...prev.scores } : {});
  const [feedback, setFeedback] = useState(prev?.feedback || "");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const complete = CRITERIA.every(([k]) => scores[k] != null);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(submitUrl, { player_id: playerId, token: token || null, scores, feedback: feedback || null, confirm });
      onDone(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <div className="space-y-6" data-testid="rating-form">
      <div className="rounded-xl glass px-4 py-3 text-sm" data-testid="rating-category">
        {note || <>Your rating counts as <b>{CAT[el.category].label}</b> ({CAT[el.category].pct}) — assigned automatically{el.event ? ` from ${el.event.name}` : ""}.</>}
      </div>
      <p className="text-xs" style={{ color: "var(--text-2)" }}>Rate each area on the 0.0–6.0+ PointControl scale. Tap a level or drag the slider; the level description is shown as a guide.</p>
      {CRITERIA.map(([k, l]) => <LevelPicker key={k} k={k} label={l} value={scores[k]} onChange={(n) => setScores((s) => ({ ...s, [k]: Math.round(n * 100) / 100 }))} />)}
      <div>
        <div className="text-sm font-semibold mb-2">Written feedback <span className="text-xs font-normal" style={{ color: "var(--text-2)" }}>(optional · visible only to the player and admins)</span></div>
        <textarea data-testid="rating-feedback" value={feedback} onChange={(e) => setFeedback(e.target.value.slice(0, 1000))} rows={3}
          className="w-full rounded-xl bg-surface glow-border px-3 py-2.5 text-sm outline-none" style={{ color: "var(--text)" }} placeholder="What stands out about their game?" />
      </div>
      <div className="rounded-xl px-4 py-3 text-sm flex gap-2" style={{ background: "rgba(168,230,0,0.12)", color: "var(--text)" }} data-testid="rating-once-warning">
        <Info className="w-4 h-4 shrink-0 mt-0.5 text-primary-c" />
        <span>{casual ? <><b>Once per session:</b> you can rate each player one time for this Casual Play session.</>
          : <><b>Good to know:</b> organizers can update their rating anytime; players and peers can update theirs once every 7 days. Every version is kept in the rating history.</>}</span>
      </div>
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" data-testid="rating-confirm-checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} /> I confirm this rating is honest.
      </label>
      <button data-testid="rating-submit-btn" disabled={!complete || !confirm || busy} onClick={submit}
        className="btn-glow rounded-full px-6 py-3 text-sm font-bold flex items-center gap-2 disabled:opacity-40">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {prev ? "Update rating" : "Submit rating"}
      </button>
    </div>
  );
}
